import { useState } from 'react';
import { Alert, Badge, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconDeviceDesktopAnalytics, IconRefresh } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { BlockLoader } from '../../components/BlockLoader';
import { AgentJadvali } from '../../components/AgentJadvali';
import { AgentOynasi } from '../../components/AgentOynasi';
import { useServerSoati } from '../../hooks/useServerSoati';
import { SILENT_ERROR } from '../../lib/queryMeta';
import {
  agentHolati, agentlarniSarala, MUAMMOLI, SUPERADMIN_AGENT_YOLLARI, type AgentRoyxatQatori,
} from '../../lib/agent';

/**
 * Platforma superadmini — barcha maktablarning FACE ID agentlari (spec 2026-10-15 agent boshqaruv §6.3):
 * so'nggi 30 kunda ko'ringan kompyuterlar, muammolilar tepada, 30 s da yangilanadi. Qator bosilsa
 * o'sha `AgentOynasi` superadmin endpointlari bilan (buyruq maktab audit jurnaliga ham tushadi).
 * So'rov yiqilsa ro'yxat o'rnida ochiq xato + "Qayta urinish": bo'sh holat ("kompyuter ko'rinmadi")
 * yolg'on bo'lardi, eski ro'yxat esa "hozir" oldinga ketgani sari sog' kompyuterlarni ham qizartirardi.
 */
export default function Agentlar() {
  const { t } = useTranslation();
  const [ochiq, setOchiq] = useState<string | null>(null);
  const { data, isLoading, isError, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['sa-agentlar'],
    queryFn: async () => (await api.get<{ agentlar: AgentRoyxatQatori[] }>('/superadmin/agentlar')).data.agentlar,
    refetchInterval: 30_000,
    // Fonda yangilanmaydi — qaytganda darhol yangilansin (belgilar eski ma'lumot bilan qizarmasin, useServerSoati)
    refetchOnWindowFocus: true,
    // Xato ro'yxat o'rnida ochiq ko'rsatiladi — 30 s lik takrorlashda toast yog'ilmasin
    meta: SILENT_ERROR,
  });
  // Saralash, "Muammoli: N" va jadval belgilari — BITTA "hozir" (server soati; 15 s da va yangi ro'yxat bilan qayta hisoblanadi)
  const hozir = useServerSoati(dataUpdatedAt);

  if (isLoading) return <BlockLoader variant="card" />;

  const agentlar = isError ? [] : agentlarniSarala(data ?? [], hozir);
  const muammoli = agentlar.filter((a) => MUAMMOLI.includes(agentHolati(a, hozir).kalit)).length;

  return (
    <Stack>
      <PageHeader title={t('nav.agentlar')} subtitle={t('agent.sahifaIzoh')} />
      <SectionCard
        title={t('nav.agentlar')}
        icon={IconDeviceDesktopAnalytics}
        right={
          !isError && (
            <Group gap={6}>
              <Badge variant="light" color="gray">{t('agent.jami', { n: agentlar.length })}</Badge>
              <Badge variant="light" color={muammoli ? 'red' : 'teal'}>{t('agent.muammoli', { n: muammoli })}</Badge>
            </Group>
          )
        }
      >
        {isError ? (
          <Stack align="center" gap="sm">
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
              {t('agent.royxatYuklanmadi')}
            </Alert>
            <Button variant="light" loading={isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
              {t('agent.qaytaUrinish')}
            </Button>
          </Stack>
        ) : agentlar.length === 0 ? (
          <Alert color="gray" variant="light">{t('agent.bosh')}</Alert>
        ) : (
          <Stack gap="xs">
            <AgentJadvali agentlar={agentlar} hozir={hozir} onTanla={setOchiq} maktabUstuni />
            <Text size="xs" c="dimmed">{t('agent.qatorniBosing')}</Text>
          </Stack>
        )}
      </SectionCard>
      <AgentOynasi agentId={ochiq} onClose={() => setOchiq(null)} yol={SUPERADMIN_AGENT_YOLLARI} buyruqBera maktabUstuni />
    </Stack>
  );
}
